import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Mic, MicOff, Volume2, ArrowLeft, Send, Loader2, CheckCircle } from 'lucide-react';
import { AuthContext } from '../App';
import CabinCrewLayout from '../layouts/CabinCrewLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SpeakingPractice = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [session, setSession] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState([]);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  
  useEffect(() => {
    startSession();
  }, []);
  
  const startSession = async () => {
    try {
      const response = await axios.post(`${API}/speaking/start-session`, {}, {
        withCredentials: true
      });
      setSession(response.data);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Önce yazılı sınav yapmalısınız!');
        navigate('/dashboard');
      } else {
        toast.error('Oturum başlatılamadı');
        navigate('/dashboard');
      }
    }
  };
  
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info('Kayıt başladı, konuşmaya başlayın...');
    } catch (error) {
      toast.error('Mikrofon erişimi reddedildi');
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      toast.success('Kayıt durduruldu');
    }
  };
  
  const submitAnswer = async () => {
    if (!audioBlob) {
      toast.error('Lütfen önce ses kaydı yapın');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // Convert audio to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        const response = await axios.post(
          `${API}/speaking/submit-answer`,
          {
            session_id: session.session_id,
            question_index: currentQuestionIndex,
            audio_data: base64Audio
          },
          { withCredentials: true }
        );
        
        setTranscript(response.data.transcript);
        setEvaluation(response.data.evaluation);
        
        // Store answer
        const newAnswer = {
          question: session.questions[currentQuestionIndex],
          transcript: response.data.transcript,
          evaluation: response.data.evaluation
        };
        setAnswers([...answers, newAnswer]);
        
        toast.success('Cevabınız değerlendirildi!');
      };
    } catch (error) {
      toast.error('Cevap gönderilemedi: ' + (error.response?.data?.detail || 'Bilinmeyen hata'));
    } finally {
      setIsProcessing(false);
    }
  };
  
  const nextQuestion = () => {
    if (currentQuestionIndex < session.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setAudioBlob(null);
      setTranscript('');
      setEvaluation(null);
    } else {
      completeSession();
    }
  };
  
  const playQuestionAudio = async () => {
    if (!session || isPlayingAudio) return;
    
    const currentQuestion = session.questions[currentQuestionIndex];
    
    // Directly use browser's speech synthesis since OpenAI TTS often fails
    // This provides a more reliable user experience
    useSpeechSynthesis(currentQuestion);
  };

  const useSpeechSynthesis = (text) => {
    if (!('speechSynthesis' in window)) {
      setIsPlayingAudio(false);
      toast.info('Ses servisi kullanılamıyor. Soruyu ekranda okuyabilirsiniz.');
      return;
    }

    try {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.9;
      
      // Wait for voices to be loaded
      const setVoice = () => {
        const voices = window.speechSynthesis.getVoices();
        const englishVoice = voices.find(v => v.lang.startsWith('en'));
        if (englishVoice) {
          utterance.voice = englishVoice;
        }
      };
      
      if (window.speechSynthesis.getVoices().length > 0) {
        setVoice();
      } else {
        window.speechSynthesis.onvoiceschanged = setVoice;
      }
      
      utterance.onstart = () => {
        setIsPlayingAudio(true);
      };
      
      utterance.onend = () => {
        setIsPlayingAudio(false);
      };
      
      utterance.onerror = (e) => {
        console.warn('Speech synthesis warning:', e.error);
        setIsPlayingAudio(false);
        // Don't show error toast for 'interrupted' or 'canceled' errors
        if (e.error !== 'interrupted' && e.error !== 'canceled') {
          toast.info('Soruyu ekranda okuyabilirsiniz.');
        }
      };
      
      // Use setTimeout to avoid immediate execution issues
      setTimeout(() => {
        try {
          window.speechSynthesis.speak(utterance);
        } catch (err) {
          console.warn('Speech synthesis speak error:', err);
          setIsPlayingAudio(false);
          toast.info('Soruyu ekranda okuyabilirsiniz.');
        }
      }, 100);
      
    } catch (err) {
      console.warn('Speech synthesis setup error:', err);
      setIsPlayingAudio(false);
      toast.info('Soruyu ekranda okuyabilirsiniz.');
    }
  };
  
  const completeSession = async () => {
    try {
      await axios.post(
        `${API}/speaking/complete-session`,
        { session_id: session.session_id },
        { withCredentials: true }
      );
      toast.success('Konuşma pratiği tamamlandı!');
      navigate(`/speaking-results/${session.session_id}`);
    } catch (error) {
      toast.error('Oturum tamamlanamadı');
    }
  };
  
  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto" />
            <p className="mt-4 text-gray-600">Oturum hazırlanıyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }
  
  if (!session) return null;
  
  const currentQuestion = session.questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / session.questions.length) * 100;
  
  return (
    <CabinCrewLayout>
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center justify-between mb-8">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Dashboard'a Dön
          </Button>
          <div className="text-right">
            <p className="text-sm text-gray-600">Seviye: <span className="font-bold text-blue-600">{session.level}</span></p>
            <p className="text-sm text-gray-600">Soru {currentQuestionIndex + 1}/{session.total_questions}</p>
          </div>
        </div>
        
        <Progress value={progress} className="mb-8" />
        
        <Card className="glass-effect border-none shadow-xl mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">Konuşma Sorusu</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 p-6 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-700">Soru:</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={playQuestionAudio}
                  disabled={isPlayingAudio || isRecording}
                  className="flex items-center gap-2"
                >
                  <Volume2 className={`w-4 h-4 ${isPlayingAudio ? 'animate-pulse' : ''}`} />
                  {isPlayingAudio ? 'Oynatılıyor...' : 'Soruyu Dinle'}
                </Button>
              </div>
              <p className="text-lg text-gray-800">{currentQuestion}</p>
            </div>
            
            <div className="flex items-center justify-center gap-4">
              {!isRecording ? (
                <Button
                  onClick={startRecording}
                  size="lg"
                  className="bg-red-500 hover:bg-red-600 text-white"
                  disabled={isProcessing || evaluation}
                >
                  <Mic className="w-5 h-5 mr-2" />
                  Kaydı Başlat
                </Button>
              ) : (
                <Button
                  onClick={stopRecording}
                  size="lg"
                  className="bg-gray-600 hover:bg-gray-700 text-white animate-pulse"
                >
                  <MicOff className="w-5 h-5 mr-2" />
                  Kaydı Durdur
                </Button>
              )}
              
              {audioBlob && !evaluation && (
                <Button
                  onClick={submitAnswer}
                  size="lg"
                  disabled={isProcessing}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isProcessing ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5 mr-2" />
                  )}
                  Cevabı Gönder
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        
        {transcript && (
          <Card className="glass-effect border-none shadow-xl mb-6">
            <CardHeader>
              <CardTitle className="text-xl">Transkript</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 italic">"{transcript}"</p>
            </CardContent>
          </Card>
        )}
        
        {evaluation && (
          <Card className="glass-effect border-none shadow-xl mb-6">
            <CardHeader>
              <CardTitle className="text-xl flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-600" />
                AI Değerlendirmesi
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Türkçe Feedback */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  🇹🇷 Türkçe Geri Bildirim:
                </h3>
                <p className="text-gray-700">{evaluation.feedback_tr}</p>
                {evaluation.suggestions_tr && evaluation.suggestions_tr.length > 0 && (
                  <div className="mt-3">
                    <p className="font-semibold text-sm mb-1">Öneriler:</p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {evaluation.suggestions_tr.map((sug, idx) => (
                        <li key={idx}>{sug}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              {/* English Feedback */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  🇬🇧 English Feedback:
                </h3>
                <p className="text-gray-700">{evaluation.feedback_en}</p>
                {evaluation.suggestions_en && evaluation.suggestions_en.length > 0 && (
                  <div className="mt-3">
                    <p className="font-semibold text-sm mb-1">Suggestions:</p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      {evaluation.suggestions_en.map((sug, idx) => (
                        <li key={idx}>{sug}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              
              {/* Corrected Version */}
              {evaluation.corrected_version && (
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-700 mb-2">✨ Geliştirilmiş Versiyon:</h3>
                  <p className="text-gray-700 italic">"{evaluation.corrected_version}"</p>
                </div>
              )}
              
              {/* Scores */}
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-gray-600">Akıcılık / Fluency</p>
                  <p className="text-2xl font-bold text-blue-600">{evaluation.fluency_score}/10</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-gray-600">Dilbilgisi / Grammar</p>
                  <p className="text-2xl font-bold text-purple-600">{evaluation.grammar_score}/10</p>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">İçerik / Content</p>
                  <p className="text-2xl font-bold text-green-600">{evaluation.content_score}/10</p>
                </div>
              </div>
              
              <div className="flex justify-center">
                <Button
                  onClick={nextQuestion}
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  {currentQuestionIndex < session.questions.length - 1 ? 'Sonraki Soru' : 'Bitir'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
    </CabinCrewLayout>
  );
};

export default SpeakingPractice;